import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getEvent, getFoodTrucks, addParticipant, removeParticipant, payEvent, updateEvent, voteOnEvent, removeVote } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Calendar,
  MapPin,
  Users,
  Euro,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  Info,
  ThumbsUp,
  ThumbsDown,
  UtensilsCrossed,
  UserPlus,
  Award,
  Send,
  Loader2,
  Mail,
  ChefHat,
  Truck,
  Zap,
  Car,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isBureau, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [addParticipantDialog, setAddParticipantDialog] = useState(false);
  const [selectedFoodTruck, setSelectedFoodTruck] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [voting, setVoting] = useState(false);
  const [canVote, setCanVote] = useState(false);
  const [staffEvenement, setStaffEvenement] = useState(null);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [pendingVote, setPendingVote] = useState(null);
  
  // State for participation request view (when food truck clicks from email)
  const [myParticipationRequest, setMyParticipationRequest] = useState(null);
  const [isParticipationView, setIsParticipationView] = useState(false);
  
  // Redevance state (admin/bureau only)
  const [showRedevanceDialog, setShowRedevanceDialog] = useState(false);
  const [redevanceConfig, setRedevanceConfig] = useState({
    type_prestation: "vente_libre",
    deal_audrey: false,
    redevance_type: "pourcentage",
    redevance_pourcentage: null,
    redevance_fixe: null,
  });
  const [savingRedevance, setSavingRedevance] = useState(false);
  
  // Participation requests state
  const [participationRequests, setParticipationRequests] = useState([]);
  const [sendingRequests, setSendingRequests] = useState(false);
  const [validatingParticipants, setValidatingParticipants] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [validatingPlacement, setValidatingPlacement] = useState(false);

  // Get current user's vote
  const myVote = event?.votes?.find(v => v.food_truck_id === user?.food_truck_id);
  
  // Check if current user is the PROPOSER of this event
  const isProposer = event?.cree_par_food_truck_id && user?.food_truck_id === event?.cree_par_food_truck_id;
  
  // Helper to check if user can manage this event (admin, bureau, or proposer)
  const canManageEvent = () => isAdmin() || isBureau() || isProposer;
  
  // Check if user can vote on this event
  const checkCanVote = async (eventData, ftList) => {
    if (!user?.food_truck_id) {
      setCanVote(false);
      return;
    }
    
    // Admins/Bureau/Proposer can always vote but never see simplified view
    if (isAdmin() || isBureau() || (user?.food_truck_id && eventData.cree_par_food_truck_id === user.food_truck_id)) {
      setCanVote(true);
      setIsParticipationView(false);
      return;
    }
    
    // For MEMBERS (non-admin/bureau): ALWAYS check participation request first
    // This ensures the simplified view is shown when clicking from email
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${eventData.id}/my-participation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_request) {
          setMyParticipationRequest(data);
          setIsParticipationView(true); // Show simplified view for members with participation request
          setCanVote(data.status === "pending" || data.status === "interested" || data.status === "not_interested");
          return;
        }
      }
    } catch (error) {
      console.error("Error checking participation status:", error);
    }
    
    // If no participation request, check specialty matching for voting eligibility
    const myFoodTruck = ftList.find(ft => ft.id === user.food_truck_id);
    if (!myFoodTruck) {
      setCanVote(false);
      return;
    }
    
    // If event is open to "Toutes" specialités, user can vote
    if (!eventData.specialites_recherchees?.length || 
        eventData.specialites_recherchees.includes("Toutes")) {
      setCanVote(true);
      return;
    }
    
    // Check if user's specialty matches the event's required specialties
    const userSpecialty = myFoodTruck.specialite || myFoodTruck.specialites?.[0];
    if (userSpecialty && eventData.specialites_recherchees.includes(userSpecialty)) {
      setCanVote(true);
      return;
    }
    
    setCanVote(false);
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [eventRes, ftRes] = await Promise.all([getEvent(id), getFoodTrucks()]);
      setEvent(eventRes.data);
      setFormData(eventRes.data);
      setFoodTrucks(ftRes.data);
      
      // Check if user can vote on this event
      await checkCanVote(eventRes.data, ftRes.data);
      
      // Load participation requests for admins when in participation workflow
      if (eventRes.data.statut === "en_attente_participation" || eventRes.data.participation_requests_sent) {
        loadParticipationRequests();
      }
    } catch (error) {
      toast.error("Erreur de chargement");
      navigate("/calendar");
    } finally {
      setLoading(false);
    }
  };
  
  const loadParticipationRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/participation-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setParticipationRequests(data || []);
        // Pre-select already interested food trucks
        const interested = (data || []).filter(r => r.status === "interested").map(r => r.food_truck_id);
        setSelectedParticipants(interested);
      }
    } catch (error) {
      console.error("Error loading participation requests:", error);
    }
  };
  
  const handleSendParticipationRequests = async () => {
    setSendingRequests(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/send-participation-requests`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.emails_sent} demande(s) de participation envoyée(s)`);
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi des demandes");
    } finally {
      setSendingRequests(false);
    }
  };
  
  const handleValidateSelectedParticipants = async () => {
    if (selectedParticipants.length === 0) {
      toast.error("Veuillez sélectionner au moins un food truck");
      return;
    }
    
    setValidatingParticipants(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/validate-participants`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ selected_food_trucks: selectedParticipants })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.participants_count} participant(s) validé(s) - ${data.emails_sent} email(s) envoyé(s)`);
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Erreur lors de la validation");
      }
    } catch (error) {
      toast.error("Erreur lors de la validation");
    } finally {
      setValidatingParticipants(false);
    }
  };
  
  const handleValidatePlacement = async () => {
    setValidatingPlacement(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/validate-placement`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Placement validé ! ${data.emails_sent} email(s) de confirmation envoyé(s)`);
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Erreur lors de la validation du placement");
      }
    } catch (error) {
      toast.error("Erreur lors de la validation du placement");
    } finally {
      setValidatingPlacement(false);
    }
  };
  
  const toggleParticipantSelection = (ftId) => {
    setSelectedParticipants(prev => 
      prev.includes(ftId) ? prev.filter(id => id !== ftId) : [...prev, ftId]
    );
  };

  // Save redevance configuration
  const handleSaveRedevance = async () => {
    setSavingRedevance(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${id}`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          type_prestation: redevanceConfig.type_prestation,
          deal_audrey: redevanceConfig.deal_audrey,
          redevance_type: redevanceConfig.redevance_type,
          redevance_pourcentage: redevanceConfig.redevance_pourcentage,
          redevance_fixe: redevanceConfig.redevance_fixe,
        })
      });
      
      if (response.ok) {
        toast.success("Configuration de redevance enregistrée");
        setShowRedevanceDialog(false);
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde de la redevance");
    } finally {
      setSavingRedevance(false);
    }
  };

  // Load redevance config when event loads
  useEffect(() => {
    if (event) {
      setRedevanceConfig({
        type_prestation: event.type_prestation || "vente_libre",
        deal_audrey: event.deal_audrey || false,
        redevance_type: event.redevance_type || "pourcentage",
        redevance_pourcentage: event.redevance_pourcentage || null,
        redevance_fixe: event.redevance_fixe || null,
      });
    }
  }, [event]);


  const handleSave = async () => {
    try {
      await updateEvent(id, {
        titre: formData.titre,
        date_debut: formData.date_debut,
        date_fin: formData.date_fin,
        lieu: formData.lieu,
        adresse: formData.adresse,
        information_complementaire: formData.information_complementaire,
        menu: formData.menu,
        type_evenement: formData.type_evenement,
        apporteur_deal_id: formData.apporteur_deal_id,
        // New fields for admin/bureau editing
        nombre_food_trucks_requis: formData.nombre_food_trucks_requis,
        specialites_recherchees: formData.specialites_recherchees,
        specialite_recherchee_autre: formData.specialite_recherchee_autre,
        frais_par_service: formData.frais_par_service,
        bureau_exempt: formData.bureau_exempt,
        type_service: formData.type_service,
        horaire_debut: formData.horaire_debut,
        horaire_fin: formData.horaire_fin,
        client: formData.client,
        contact_telephone: formData.contact_telephone,
      });
      toast.success("Événement mis à jour");
      navigate("/calendar"); // Redirect to calendar after save
    } catch (error) {
      toast.error("Erreur de mise à jour");
    }
  };

  const handleAddParticipant = async () => {
    if (!selectedFoodTruck) return;
    try {
      const result = await addParticipant(id, selectedFoodTruck);
      toast.success(`Participant ajouté${result.data.montant_du > 0 ? ` (+${result.data.montant_du}€)` : ""}`);
      setAddParticipantDialog(false);
      setSelectedFoodTruck("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'ajout");
    }
  };

  const handleRemoveParticipant = async () => {
    if (!removeTarget) return;
    try {
      await removeParticipant(id, removeTarget);
      toast.success("Participant retiré");
      setRemoveTarget(null);
      loadData();
    } catch (error) {
      toast.error("Erreur de suppression");
    }
  };

  const handlePayParticipant = async () => {
    if (!payTarget) return;
    try {
      await payEvent(id, payTarget);
      toast.success("Paiement enregistré");
      setPayTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de paiement");
    }
  };

  const handleVote = async (vote, staffCount = null) => {
    if (!user?.food_truck_id) {
      toast.error("Vous devez être associé à un food truck pour voter");
      return;
    }
    
    // For "interested" votes, show staff dialog first if not provided
    if (vote === "interested" && staffCount === null) {
      setPendingVote(vote);
      // Get user's food truck to pre-fill staff
      const myFt = foodTrucks.find(ft => ft.id === user.food_truck_id);
      setStaffEvenement(myFt?.staff_base || 2);
      setShowStaffDialog(true);
      return;
    }
    
    setVoting(true);
    try {
      await voteOnEvent(id, vote, vote === "interested" ? staffCount : null);
      toast.success(vote === "interested" ? "Vous êtes intéressé !" : "Vous n'êtes pas intéressé");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du vote");
    } finally {
      setVoting(false);
      setShowStaffDialog(false);
      setPendingVote(null);
    }
  };
  
  const confirmStaffAndVote = async () => {
    await handleVote(pendingVote, staffEvenement);
  };

  const handleRemoveVote = async () => {
    setVoting(true);
    try {
      await removeVote(id);
      toast.success("Vote annulé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    } finally {
      setVoting(false);
    }
  };

  // Add participant from interested votes
  const handleAddFromVote = async (ftId) => {
    try {
      const result = await addParticipant(id, ftId);
      toast.success(`Food truck sélectionné pour l'événement${result.data.montant_du > 0 ? ` (+${result.data.montant_du}€)` : ""}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sélection");
    }
  };

  // Get available food trucks (not already participants)
  const availableFoodTrucks = foodTrucks.filter(
    (ft) => ft.statut === "actif" && !event?.participants.some((p) => p.food_truck_id === ft.id)
  );

  // Vote counts
  const interestedVotes = event?.votes?.filter(v => v.vote === "interested") || [];
  const notInterestedVotes = event?.votes?.filter(v => v.vote === "not_interested") || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!event) return null;

  // ====== SIMPLIFIED VIEW FOR FOOD TRUCKS RESPONDING TO PARTICIPATION REQUEST ======
  // When a food truck clicks from the email, show ONLY the essential info and response buttons
  const isMemberParticipationView = isParticipationView && !(isAdmin() || isBureau()) && user?.food_truck_id;
  
  if (isMemberParticipationView) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="participation-response-view">
        {/* Simple Header */}
        <div className="text-center space-y-2">
          <h1 className="font-heading text-2xl sm:text-3xl font-black text-primary">
            Demande de participation
          </h1>
          <p className="text-muted-foreground">
            Vous avez été sollicité(e) pour participer à cet événement
          </p>
        </div>

        {/* Event Details Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl sm:text-2xl text-secondary flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              {event.titre}
            </CardTitle>
            <Badge 
              variant="default"
              className="w-fit mt-2"
            >
              {event.type_evenement}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date */}
            <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg">
              <Calendar className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-secondary">Date</p>
                <p className="text-muted-foreground">
                  {event.date_debut === event.date_fin 
                    ? format(parseISO(event.date_debut), "EEEE dd MMMM yyyy", { locale: fr })
                    : `Du ${format(parseISO(event.date_debut), "dd MMMM", { locale: fr })} au ${format(parseISO(event.date_fin), "dd MMMM yyyy", { locale: fr })}`}
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-secondary">Lieu</p>
                <p className="text-muted-foreground">{event.lieu}</p>
                {event.adresse && <p className="text-sm text-muted-foreground">{event.adresse}</p>}
              </div>
            </div>

            {/* Food trucks needed */}
            {event.nombre_food_trucks_requis && (
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg">
                <Truck className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-secondary">Food trucks recherchés</p>
                  <p className="text-muted-foreground">{event.nombre_food_trucks_requis} food truck(s)</p>
                </div>
              </div>
            )}

            {/* Specialties */}
            {event.specialites_recherchees?.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg">
                <ChefHat className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-secondary">Spécialités recherchées</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {event.specialites_recherchees.map((spec, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Additional info */}
            {event.information_complementaire && (
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg">
                <Info className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-secondary">Informations complémentaires</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{event.information_complementaire}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Section */}
        <Card className="border-2 border-primary/20 shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-secondary">
              {myParticipationRequest?.status === "pending" ? (
                "Souhaitez-vous participer ?"
              ) : myParticipationRequest?.status === "interested" ? (
                <span className="text-status-success flex items-center justify-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  Vous avez confirmé votre intérêt !
                </span>
              ) : myParticipationRequest?.status === "not_interested" ? (
                <span className="text-muted-foreground flex items-center justify-center gap-2">
                  <X className="w-6 h-6" />
                  Vous avez décliné cette participation
                </span>
              ) : myParticipationRequest?.is_participant ? (
                <span className="text-status-success flex items-center justify-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  Vous êtes confirmé(e) pour cet événement !
                </span>
              ) : (
                "Votre réponse"
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show response buttons only if pending or can change vote */}
            {(myParticipationRequest?.status === "pending" || canVote) && !myParticipationRequest?.is_participant && (
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  onClick={() => handleVote("interested")}
                  disabled={voting}
                  size="lg"
                  className={`flex-1 sm:flex-none min-w-[180px] ${
                    myVote?.vote === "interested" || myParticipationRequest?.status === "interested"
                      ? "bg-status-success hover:bg-status-success/90" 
                      : "bg-white border-2 border-status-success text-status-success hover:bg-status-success hover:text-white"
                  }`}
                  variant={myVote?.vote === "interested" || myParticipationRequest?.status === "interested" ? "default" : "outline"}
                  data-testid="participate-yes-btn"
                >
                  <ThumbsUp className="w-5 h-5 mr-2" />
                  Oui, je suis intéressé(e)
                </Button>
                <Button
                  onClick={() => handleVote("not_interested")}
                  disabled={voting}
                  size="lg"
                  className={`flex-1 sm:flex-none min-w-[180px] ${
                    myVote?.vote === "not_interested" || myParticipationRequest?.status === "not_interested"
                      ? "bg-status-error hover:bg-status-error/90" 
                      : "bg-white border-2 border-status-error text-status-error hover:bg-status-error hover:text-white"
                  }`}
                  variant={myVote?.vote === "not_interested" || myParticipationRequest?.status === "not_interested" ? "default" : "outline"}
                  data-testid="participate-no-btn"
                >
                  <ThumbsDown className="w-5 h-5 mr-2" />
                  Non merci
                </Button>
              </div>
            )}

            {/* Status message after response */}
            {myParticipationRequest?.status === "interested" && !myParticipationRequest?.is_participant && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <p className="text-green-700">
                  <strong>Merci !</strong> Votre intérêt a été enregistré. 
                  Vous serez notifié(e) si vous êtes sélectionné(e) pour cet événement.
                </p>
              </div>
            )}

            {myParticipationRequest?.is_participant && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <p className="text-green-700">
                  <strong>Félicitations !</strong> Vous êtes confirmé(e) pour cet événement.
                  Retrouvez-le dans votre espace "Mes Services".
                </p>
                <Button
                  onClick={() => navigate("/mes-services")}
                  className="mt-3"
                  variant="outline"
                >
                  Voir mes services
                </Button>
              </div>
            )}

            {/* Cancel vote option */}
            {myVote && !myParticipationRequest?.is_participant && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveVote}
                  disabled={voting}
                  className="text-muted-foreground"
                >
                  Modifier ma réponse
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Link to see more details */}
        <div className="text-center">
          <Button
            variant="link"
            onClick={() => setIsParticipationView(false)}
            className="text-muted-foreground"
          >
            Voir tous les détails de l'événement
          </Button>
        </div>
      </div>
    );
  }

  // ====== FULL VIEW FOR ADMIN/BUREAU OR WHEN USER CLICKS "SEE MORE DETAILS" ======
  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="event-detail">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-2 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/calendar")}
          className="text-secondary hover:text-primary flex-shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-primary truncate">
            {event.titre}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1 sm:gap-2 mt-1">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" /> 
            <span className="truncate">
              {event.date_debut === event.date_fin 
                ? format(parseISO(event.date_debut), "dd MMM yyyy", { locale: fr })
                : `${format(parseISO(event.date_debut), "dd MMM", { locale: fr })} - ${format(parseISO(event.date_fin), "dd MMM yyyy", { locale: fr })}`}
            </span>
          </p>
        </div>
        <Badge 
          variant={event.type_evenement === "collectif" ? "default" : "secondary"}
          className="text-xs px-2 py-1 sm:text-sm sm:px-4 flex-shrink-0"
        >
          {event.type_evenement}
        </Badge>
      </div>

      {/* Participation Workflow Section - For Admin/Bureau/Proposer */}
      {canManageEvent() && (event.statut === "en_attente" || event.statut === "en_attente_participation" || event.statut === "en_attente_placement") && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardHeader className="pb-2 p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex flex-col sm:flex-row sm:items-center gap-2 text-amber-800">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                Workflow de participation
                {isProposer && !isAdmin() && !isBureau() && (
                  <Badge variant="outline" className="ml-2 text-xs border-amber-400 text-amber-700">Vous avez proposé cet événement</Badge>
                )}
              </div>
              {event.specialites_recherchees?.length > 0 && !event.specialites_recherchees.includes("Toutes") && (
                <div className="flex flex-wrap gap-1 sm:ml-auto">
                  {event.specialites_recherchees.map((spec, i) => (
                    <Badge key={i} variant="outline" className="border-orange-400 text-orange-600 bg-orange-50 text-[10px] sm:text-xs">
                      <ChefHat className="w-2 h-2 sm:w-3 sm:h-3 mr-1" />{spec}
                    </Badge>
                  ))}
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 pt-0">
            {/* Step 1: Validate proposal - show only for en_attente status AND only for bureau/admin */}
            {event.statut === "en_attente" && !event.participation_requests_sent && (isAdmin() || isBureau()) && (
              <div className="bg-white p-3 sm:p-4 rounded-lg border border-amber-200">
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                  <strong>Étape 1 :</strong> Validez la proposition pour envoyer les demandes
                  {event.specialites_recherchees?.length > 0 && !event.specialites_recherchees.includes("Toutes") && (
                    <span className="text-orange-600 block sm:inline"> (aux FT : {event.specialites_recherchees.join(", ")})</span>
                  )}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3">
                  En cliquant sur "Valider", les demandes de participation seront envoyées par email aux food trucks concernés.
                </p>
              </div>
            )}
            
            {/* Proposer waiting for validation message */}
            {event.statut === "en_attente" && !event.participation_requests_sent && isProposer && !isAdmin() && !isBureau() && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>En attente de validation</strong> - Le bureau doit valider votre proposition avant d'envoyer les demandes aux food trucks.
                </p>
              </div>
            )}
            
            {/* Step 2 & 3: Show only for en_attente_participation status */}
            {event.statut === "en_attente_participation" && (
              <>
                {/* Step 2: Show responses */}
                <div className="bg-white p-4 rounded-lg border border-amber-200">
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Étape 2 :</strong> Réponses reçues ({participationRequests.filter(r => r.status !== "pending").length}/{participationRequests.length})
                  </p>
                  
                  {participationRequests.length > 0 ? (
                    <div className="space-y-2">
                      {/* Interested */}
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-green-700">Intéressés :</p>
                        {participationRequests.filter(r => r.status === "interested").map((req) => {
                          const ft = foodTrucks.find(f => f.id === req.food_truck_id);
                          const isProposeur = req.is_proposeur || req.food_truck_id === event?.cree_par_food_truck_id;
                          return (
                            <div key={req.food_truck_id} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                              <input
                                type="checkbox"
                                checked={selectedParticipants.includes(req.food_truck_id)}
                                onChange={() => toggleParticipantSelection(req.food_truck_id)}
                                className="w-4 h-4 text-green-600"
                              />
                              <ThumbsUp className="w-4 h-4 text-green-600" />
                              <span className="font-medium">{ft?.nom_food_truck || req.food_truck_id}</span>
                              {isProposeur && (
                                <Badge className="bg-primary text-white text-xs ml-1">Proposeur</Badge>
                              )}
                            </div>
                          );
                        })}
                        {participationRequests.filter(r => r.status === "interested").length === 0 && (
                          <p className="text-sm text-gray-500 italic">Aucune réponse positive pour le moment</p>
                        )}
                      </div>
                      
                      {/* Not interested */}
                      {participationRequests.filter(r => r.status === "not_interested").length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-semibold text-red-700">Pas intéressés :</p>
                          {participationRequests.filter(r => r.status === "not_interested").map((req) => {
                            const ft = foodTrucks.find(f => f.id === req.food_truck_id);
                            return (
                              <div key={req.food_truck_id} className="flex items-center gap-2 p-2 bg-red-50 rounded opacity-70">
                                <ThumbsDown className="w-4 h-4 text-red-600" />
                                <span>{ft?.nom_food_truck || req.food_truck_id}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Pending */}
                      {participationRequests.filter(r => r.status === "pending").length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-semibold text-gray-600">En attente de réponse :</p>
                          {participationRequests.filter(r => r.status === "pending").map((req) => {
                            const ft = foodTrucks.find(f => f.id === req.food_truck_id);
                            return (
                              <div key={req.food_truck_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-600">{ft?.nom_food_truck || req.food_truck_id}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Chargement des réponses...</p>
                  )}
                </div>
                
                {/* Step 3: Validate selection */}
                <div className="bg-white p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Étape 3 :</strong> Sélectionner les participants ({selectedParticipants.length})
                    {event.nombre_food_trucks_requis && (
                      <span className="text-orange-600"> / {event.nombre_food_trucks_requis} demandés</span>
                    )}
                  </p>
                  <Button
                    onClick={handleValidateSelectedParticipants}
                    disabled={validatingParticipants || selectedParticipants.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="validate-participants-btn"
                  >
                    {validatingParticipants ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4 mr-2" />
                    )}
                    Confirmer la sélection
                  </Button>
                </div>
              </>
            )}
            
            {/* Step 4: Validate placement - Bureau only - show only for en_attente_placement status */}
            {event.statut === "en_attente_placement" && (
              <div className="bg-white p-4 rounded-lg border border-purple-300">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Étape 4 :</strong> Validation du placement par le Bureau
                    </p>
                    <p className="text-xs text-gray-500">
                      Les participants ont été sélectionnés. Validez le placement final pour envoyer les emails de confirmation.
                    </p>
                  </div>
                </div>
                
                {/* Show selected participants */}
                <div className="bg-purple-50 p-3 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-purple-700 mb-2">
                    Participants sélectionnés ({event.participants?.length || 0}) :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(event.participants || []).map((participant, idx) => {
                      // participant can be either a string (ID) or an object
                      const ftId = typeof participant === 'string' ? participant : participant?.food_truck_id;
                      const ft = foodTrucks.find(f => f.id === ftId);
                      const displayName = ft?.nom_food_truck || 
                                          (typeof participant === 'object' ? participant?.nom_food_truck : ftId) ||
                                          ftId;
                      return (
                        <Badge key={idx} variant="outline" className="border-purple-400 text-purple-700 bg-white">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {displayName}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                
                <Button
                  onClick={handleValidatePlacement}
                  disabled={validatingPlacement}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                  data-testid="validate-placement-btn"
                >
                  {validatingPlacement ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Valider le placement et envoyer les confirmations
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event Details */}
        <Card className="lg:col-span-2 bg-white border-[#E5E0DC]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl text-secondary">
              Détails de l'événement
            </CardTitle>
            {canManageEvent() && (
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(false); setFormData(event); }}>
                      <X className="w-4 h-4 mr-1" /> Annuler
                    </Button>
                    <Button size="sm" className="bg-primary hover:bg-primary-hover" onClick={handleSave} data-testid="save-event-btn">
                      <Save className="w-4 h-4 mr-1" /> Enregistrer
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-event-btn">
                      <Edit className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    {(isAdmin() || isBureau()) && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowRedevanceDialog(true)}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        data-testid="config-redevance-btn"
                      >
                        <Euro className="w-4 h-4 mr-1" /> Redevance
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-secondary font-semibold">Titre</Label>
                {editing ? (
                  <Input
                    value={formData.titre}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground font-medium">{event.titre}</p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold">Type</Label>
                {editing ? (
                  <Select 
                    value={formData.type_evenement} 
                    onValueChange={(val) => setFormData({ ...formData, type_evenement: val })}
                  >
                    <SelectTrigger className="mt-1 border-[#E5E0DC]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collectif">Collectif</SelectItem>
                      <SelectItem value="individuel">Individuel</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1 text-foreground capitalize">{event.type_evenement}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date de début
                </Label>
                {editing ? (
                  <Input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{format(parseISO(event.date_debut), "dd MMMM yyyy", { locale: fr })}</p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date de fin
                </Label>
                {editing ? (
                  <Input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{format(parseISO(event.date_fin), "dd MMMM yyyy", { locale: fr })}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Lieu
                </Label>
                {editing ? (
                  <Input
                    value={formData.lieu}
                    onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{event.lieu}</p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold">Adresse</Label>
                {editing ? (
                  <Input
                    value={formData.adresse || ""}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{event.adresse || "Non renseignée"}</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <Info className="w-4 h-4" /> Informations complémentaires
              </Label>
              {editing ? (
                <Textarea
                  value={formData.information_complementaire || ""}
                  onChange={(e) => setFormData({ ...formData, information_complementaire: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  rows={3}
                />
              ) : (
                <p className="mt-1 text-foreground">{event.information_complementaire || "Aucune information"}</p>
              )}
            </div>

            <div>
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4" /> Menu
              </Label>
              {editing ? (
                <Textarea
                  value={formData.menu || ""}
                  onChange={(e) => setFormData({ ...formData, menu: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  rows={2}
                />
              ) : (
                <p className="mt-1 text-foreground">{event.menu || "Non renseigné"}</p>
              )}
            </div>

            {/* Spécialités recherchées */}
            <div className="border-t border-[#E5E0DC] pt-6">
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-orange-600" /> Spécialité(s) recherchée(s)
              </Label>
              {editing ? (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {["Burger", "Pizza", "Asiatique", "Mexicain", "Crêpes", "Végétarien", "Fruits de mer", "Grillades", "Desserts", "Boissons", "Toutes"].map((spec) => (
                      <label key={spec} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox 
                          checked={(formData.specialites_recherchees || []).includes(spec)}
                          onCheckedChange={(checked) => {
                            const current = formData.specialites_recherchees || [];
                            if (checked) {
                              setFormData({ ...formData, specialites_recherchees: [...current, spec] });
                            } else {
                              setFormData({ ...formData, specialites_recherchees: current.filter(s => s !== spec) });
                            }
                          }}
                        />
                        <span className="text-sm">{spec}</span>
                      </label>
                    ))}
                  </div>
                  <Input
                    placeholder="Autre spécialité..."
                    value={formData.specialite_recherchee_autre || ""}
                    onChange={(e) => setFormData({ ...formData, specialite_recherchee_autre: e.target.value })}
                    className="mt-2 border-[#E5E0DC]"
                  />
                </div>
              ) : (
                <>
                  {event.specialites_recherchees?.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {event.specialites_recherchees.map((spec, i) => (
                        <Badge key={i} variant="outline" className="border-orange-400 text-orange-600 bg-orange-50">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-muted-foreground">Toutes spécialités</p>
                  )}
                  {event.specialite_recherchee_autre && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Autre : {event.specialite_recherchee_autre}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Nombre de Food Trucks requis */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-[#E5E0DC] pt-6">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Truck className="w-4 h-4 text-primary" /> Nombre de Food Trucks requis
                </Label>
                {editing ? (
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.nombre_food_trucks_requis || ""}
                    onChange={(e) => setFormData({ ...formData, nombre_food_trucks_requis: parseInt(e.target.value) || null })}
                    className="mt-1 border-[#E5E0DC] w-32"
                    placeholder="Ex: 3"
                  />
                ) : (
                  <p className="mt-1 text-foreground font-medium">
                    {event.nombre_food_trucks_requis ? `${event.nombre_food_trucks_requis} food truck(s)` : "Non spécifié"}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4 text-green-600" /> Frais par service
                </Label>
                {editing ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="0"
                      step="5"
                      value={formData.frais_par_service ?? 20}
                      onChange={(e) => setFormData({ ...formData, frais_par_service: parseFloat(e.target.value) || 0 })}
                      className="border-[#E5E0DC] w-24"
                    />
                    <span className="text-muted-foreground">€</span>
                    <label className="flex items-center gap-2 ml-4 cursor-pointer">
                      <Checkbox 
                        checked={formData.bureau_exempt ?? true}
                        onCheckedChange={(checked) => setFormData({ ...formData, bureau_exempt: checked })}
                      />
                      <span className="text-sm">Bureau exempté</span>
                    </label>
                  </div>
                ) : (
                  <p className="mt-1 text-foreground">
                    {event.frais_par_service || 0}€ {event.bureau_exempt && <span className="text-xs text-muted-foreground">(Bureau exempté)</span>}
                  </p>
                )}
              </div>
            </div>

            {/* Horaires */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-[#E5E0DC] pt-6">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Type de service
                </Label>
                {editing ? (
                  <Select 
                    value={formData.type_service || "midi"} 
                    onValueChange={(val) => setFormData({ ...formData, type_service: val })}
                  >
                    <SelectTrigger className="mt-1 border-[#E5E0DC]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="midi">Midi</SelectItem>
                      <SelectItem value="soir">Soir</SelectItem>
                      <SelectItem value="journee">Journée complète</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1 text-foreground capitalize">{event.type_service || "midi"}</p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold">Heure début</Label>
                {editing ? (
                  <Input
                    type="time"
                    value={formData.horaire_debut || "11:00"}
                    onChange={(e) => setFormData({ ...formData, horaire_debut: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{event.horaire_debut || "11:00"}</p>
                )}
              </div>
              <div>
                <Label className="text-secondary font-semibold">Heure fin</Label>
                {editing ? (
                  <Input
                    type="time"
                    value={formData.horaire_fin || "15:00"}
                    onChange={(e) => setFormData({ ...formData, horaire_fin: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                  />
                ) : (
                  <p className="mt-1 text-foreground">{event.horaire_fin || "15:00"}</p>
                )}
              </div>
            </div>

            {/* Contact Client */}
            {canManageEvent() && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-[#E5E0DC] pt-6">
                <div>
                  <Label className="text-secondary font-semibold">Client / Organisateur</Label>
                  {editing ? (
                    <Input
                      value={formData.client || ""}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Nom du client"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{event.client || "Non renseigné"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Contact téléphone</Label>
                  {editing ? (
                    <Input
                      value={formData.contact_telephone || ""}
                      onChange={(e) => setFormData({ ...formData, contact_telephone: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="06..."
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{event.contact_telephone || "Non renseigné"}</p>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-[#E5E0DC] pt-6">
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-accent" /> Apporteur du deal
              </Label>
              {editing ? (
                <Select 
                  value={formData.apporteur_deal_id || "none"} 
                  onValueChange={(val) => setFormData({ ...formData, apporteur_deal_id: val === "none" ? null : val })}
                >
                  <SelectTrigger className="mt-1 border-[#E5E0DC]">
                    <SelectValue placeholder="Sélectionner l'apporteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {foodTrucks.filter(ft => ft.statut === "actif").map((ft) => (
                      <SelectItem key={ft.id} value={ft.id}>
                        {ft.nom_food_truck}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-1">
                  {event.apporteur_deal_nom ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 text-accent rounded-full font-medium">
                      <Award className="w-4 h-4" /> {event.apporteur_deal_nom}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Non renseigné</span>
                  )}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sondage / Vote Section - Visible pour tous les membres */}
        <Card className="bg-white border-[#E5E0DC] border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-primary" />
              Sondage : Êtes-vous intéressé par cet événement ?
              {event?.nombre_food_trucks_requis && (
                <Badge variant="outline" className="ml-auto border-primary text-primary">
                  {event.nombre_food_trucks_requis} food truck(s) demandé(s)
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Voting buttons for members - Only show if user can vote */}
            {user?.food_truck_id && canVote && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleVote("interested")}
                    disabled={voting}
                    className={`${
                      myVote?.vote === "interested" 
                        ? "bg-status-success hover:bg-status-success/90" 
                        : "bg-white border-status-success text-status-success hover:bg-status-success/10"
                    }`}
                    variant={myVote?.vote === "interested" ? "default" : "outline"}
                    data-testid="vote-interested-btn"
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    Intéressé
                  </Button>
                  <Button
                    onClick={() => handleVote("not_interested")}
                    disabled={voting}
                    className={`${
                      myVote?.vote === "not_interested" 
                        ? "bg-status-error hover:bg-status-error/90" 
                        : "bg-white border-status-error text-status-error hover:bg-status-error/10"
                    }`}
                    variant={myVote?.vote === "not_interested" ? "default" : "outline"}
                    data-testid="vote-not-interested-btn"
                  >
                    <ThumbsDown className="w-5 h-5 mr-2" />
                    Pas intéressé
                  </Button>
                </div>
                
                {myVote && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveVote}
                    disabled={voting}
                    className="text-muted-foreground"
                  >
                    Annuler mon vote
                  </Button>
                )}
              </div>
            )}
            
            {/* Message when user cannot vote */}
            {user?.food_truck_id && !canVote && !(isAdmin() || isBureau()) && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Vous n'êtes pas concerné par ce sondage (spécialité non recherchée pour cet événement).
                </p>
              </div>
            )}

            {/* Vote summary */}
            <div className="pt-4 border-t border-[#E5E0DC]">
              <p className="text-sm font-semibold text-secondary mb-3">
                Résumé des réponses ({event?.votes?.length || 0} vote(s))
                {event?.nombre_food_trucks_requis && (
                  <span className="text-muted-foreground ml-2">
                    - Besoin de {event.nombre_food_trucks_requis} food truck(s)
                  </span>
                )}
              </p>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-status-success/10 flex items-center justify-center">
                    <ThumbsUp className="w-4 h-4 text-status-success" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-status-success">{interestedVotes.length}</p>
                    <p className="text-xs text-muted-foreground">Intéressés</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-status-error/10 flex items-center justify-center">
                    <ThumbsDown className="w-4 h-4 text-status-error" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-status-error">{notInterestedVotes.length}</p>
                    <p className="text-xs text-muted-foreground">Pas intéressés</p>
                  </div>
                </div>
              </div>

              {/* List of voters and add from interested (for admins/bureau/proposer) */}
              {canManageEvent() && event?.votes?.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-semibold text-secondary">Food trucks intéressés :</p>
                  <div className="grid grid-cols-1 gap-2">
                    {interestedVotes.map((v) => {
                      const isAlreadyParticipant = event?.participants?.some(p => p.food_truck_id === v.food_truck_id);
                      return (
                        <div key={v.food_truck_id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-status-success/5 rounded-lg border border-status-success/20">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <div className="flex items-center gap-2">
                              <ThumbsUp className="w-4 h-4 text-status-success" />
                              <span className="text-sm font-medium">{v.nom_food_truck}</span>
                            </div>
                            {/* Equipment info badges */}
                            <div className="flex flex-wrap gap-2 mt-1 sm:mt-0">
                              {v.type_vehicule && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {v.type_vehicule === "Camion" ? <Truck className="w-3 h-3 mr-1" /> : <Car className="w-3 h-3 mr-1" />}
                                  {v.type_vehicule}
                                </Badge>
                              )}
                              {v.plaque_immatriculation && (
                                <Badge variant="outline" className="text-xs font-mono bg-gray-50">
                                  {v.plaque_immatriculation}
                                </Badge>
                              )}
                              {v.puissance_electrique && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  <Zap className="w-3 h-3 mr-1" />
                                  {v.puissance_electrique} kW
                                </Badge>
                              )}
                              {v.staff_evenement && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  <Users className="w-3 h-3 mr-1" />
                                  {v.staff_evenement} pers.
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 sm:mt-0">
                            {isAlreadyParticipant ? (
                              <Badge className="bg-status-success text-white text-xs">Sélectionné</Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAddFromVote(v.food_truck_id)}
                                className="border-status-success text-status-success hover:bg-status-success/10 text-xs"
                                data-testid={`add-from-vote-${v.food_truck_id}`}
                              >
                                <UserPlus className="w-3 h-3 mr-1" /> Sélectionner
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {notInterestedVotes.length > 0 && (
                    <>
                      <p className="text-sm font-semibold text-secondary mt-4">Food trucks non intéressés :</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {notInterestedVotes.map((v) => (
                          <div key={v.food_truck_id} className="flex items-center gap-2 p-2 bg-status-error/5 rounded opacity-60">
                            <ThumbsDown className="w-4 h-4 text-status-error" />
                            <span className="text-sm">{v.nom_food_truck}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Participants */}
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Participants ({event.participants.length})
            </CardTitle>
            {canManageEvent() && availableFoodTrucks.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddParticipantDialog(true)}
                className="border-primary text-primary hover:bg-primary/10"
                data-testid="add-participant-btn"
              >
                <UserPlus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {event.type_evenement === "collectif" && (
              <div className="mb-4 p-3 bg-status-info/10 rounded-lg text-sm text-status-info">
                <Info className="w-4 h-4 inline mr-2" />
                Événement collectif : 20€ par participant
              </div>
            )}
            
            {event.participants.length > 0 ? (
              <div className="space-y-3">
                {event.participants.map((p) => (
                  <div
                    key={p.food_truck_id}
                    className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg"
                    data-testid={`participant-${p.food_truck_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-secondary truncate">{p.nom_food_truck}</p>
                      {p.montant_du > 0 && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Euro className="w-3 h-3" /> {p.montant_du} €
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {p.statut_paiement === "paye" ? (
                        <Badge className="bg-status-success text-white">
                          <CheckCircle className="w-3 h-3 mr-1" /> Payé
                        </Badge>
                      ) : p.statut_paiement === "en_attente" ? (
                        <>
                          <Badge variant="secondary" className="bg-status-warning text-white">
                            <Clock className="w-3 h-3 mr-1" /> En attente
                          </Badge>
                          {(isAdmin() || isBureau()) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPayTarget(p.food_truck_id)}
                              className="text-status-success hover:text-status-success/80"
                              data-testid={`pay-participant-${p.food_truck_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <Badge variant="outline">N/A</Badge>
                      )}
                      {canManageEvent() && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setRemoveTarget(p.food_truck_id)}
                          className="text-status-error hover:text-status-error/80"
                          data-testid={`remove-participant-${p.food_truck_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">Aucun participant</p>
            )}

            {/* Summary */}
            {event.participants.length > 0 && event.type_evenement === "collectif" && (
              <div className="mt-4 pt-4 border-t border-[#E5E0DC]">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total frais</span>
                  <span className="font-bold text-secondary">
                    {event.participants.reduce((sum, p) => sum + p.montant_du, 0)} €
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-muted-foreground">Payés</span>
                  <span className="font-bold text-status-success">
                    {event.participants.filter((p) => p.statut_paiement === "paye").reduce((sum, p) => sum + p.montant_du, 0)} €
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Participant Dialog */}
      <Dialog open={addParticipantDialog} onOpenChange={setAddParticipantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-primary">
              Ajouter un participant
            </DialogTitle>
            <DialogDescription>
              {event.type_evenement === "collectif" && "20€ seront automatiquement ajoutés à son solde dû"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-secondary font-semibold">Food Truck</Label>
              <Select value={selectedFoodTruck} onValueChange={setSelectedFoodTruck}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]" data-testid="select-food-truck">
                  <SelectValue placeholder="Sélectionner un food truck" />
                </SelectTrigger>
                <SelectContent>
                  {availableFoodTrucks.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>
                      {ft.nom_food_truck}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddParticipantDialog(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-primary hover:bg-primary-hover" 
              onClick={handleAddParticipant}
              disabled={!selectedFoodTruck}
              data-testid="confirm-add-participant-btn"
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Participant Confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce participant ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le montant dû sera déduit de son solde si non payé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveParticipant}
              className="bg-status-error hover:bg-status-error/90"
            >
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pay Confirmation */}
      <AlertDialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le paiement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action marquera les frais comme payés pour ce participant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePayParticipant}
              className="bg-status-success hover:bg-status-success/90"
              data-testid="confirm-pay-btn"
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff Dialog for voting */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Nombre de personnes présentes
            </DialogTitle>
            <DialogDescription>
              Indiquez combien de personnes seront présentes sur cet événement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-secondary font-semibold">Staff pour cet événement</Label>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setStaffEvenement(Math.max(1, (staffEvenement || 1) - 1))}
                  disabled={staffEvenement <= 1}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={staffEvenement || ""}
                  onChange={(e) => setStaffEvenement(parseInt(e.target.value) || 1)}
                  className="w-20 text-center font-bold text-lg"
                  data-testid="input-staff-evenement"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setStaffEvenement((staffEvenement || 1) + 1)}
                  disabled={staffEvenement >= 20}
                >
                  +
                </Button>
                <span className="text-muted-foreground">personne(s)</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Vous pouvez ajuster ce nombre pour cet événement spécifique
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={confirmStaffAndVote}
              disabled={voting}
              className="bg-status-success hover:bg-status-success/90"
              data-testid="confirm-staff-vote-btn"
            >
              {voting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <ThumbsUp className="w-4 h-4 mr-2" />
              Confirmer mon intérêt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Configuration Redevance - Admin/Bureau only */}
      <Dialog open={showRedevanceDialog} onOpenChange={setShowRedevanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-purple-600" />
              Configuration Redevance
            </DialogTitle>
            <DialogDescription>
              Définissez la commission pour cet événement. Une facture sera automatiquement générée après le bilan de prestation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type de prestation */}
            <div>
              <Label>Type de prestation</Label>
              <Select
                value={redevanceConfig.type_prestation}
                onValueChange={(v) => setRedevanceConfig({ ...redevanceConfig, type_prestation: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Type de prestation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vente_libre">Vente libre</SelectItem>
                  <SelectItem value="privatisation">Privatisation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Deal Audrey */}
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Checkbox
                id="deal_audrey_detail"
                checked={redevanceConfig.deal_audrey}
                onCheckedChange={(v) => {
                  if (v) {
                    const pct = redevanceConfig.type_prestation === "vente_libre" ? 10 : 7;
                    setRedevanceConfig({ ...redevanceConfig, deal_audrey: v, redevance_type: "pourcentage", redevance_pourcentage: pct });
                  } else {
                    setRedevanceConfig({ ...redevanceConfig, deal_audrey: v });
                  }
                }}
              />
              <div>
                <Label htmlFor="deal_audrey_detail" className="text-purple-700 font-medium cursor-pointer">
                  Deal Audrey
                </Label>
                <p className="text-xs text-purple-600">
                  {redevanceConfig.type_prestation === "vente_libre" ? "10%" : "7%"} de redevance automatique
                </p>
              </div>
            </div>
            
            {/* Configuration manuelle si pas Deal Audrey */}
            {!redevanceConfig.deal_audrey && (
              <div className="space-y-3">
                <div>
                  <Label>Type de redevance</Label>
                  <Select
                    value={redevanceConfig.redevance_type}
                    onValueChange={(v) => setRedevanceConfig({ ...redevanceConfig, redevance_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pourcentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixe">Montant fixe (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {redevanceConfig.redevance_type === "pourcentage" ? (
                  <div>
                    <Label>Pourcentage sur CA HT (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={redevanceConfig.redevance_pourcentage || ""}
                      onChange={(e) => setRedevanceConfig({ ...redevanceConfig, redevance_pourcentage: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="Ex: 10"
                      className="mt-1"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Montant fixe (€)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={redevanceConfig.redevance_fixe || ""}
                      onChange={(e) => setRedevanceConfig({ ...redevanceConfig, redevance_fixe: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="Ex: 50"
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Info sur la facturation */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Facturation automatique :</strong> Une facture sera générée pour chaque food truck après la soumission de son bilan de prestation (CA déclaré).
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedevanceDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveRedevance}
              disabled={savingRedevance}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingRedevance && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
